import React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import DocCardImage from "../DocCardImage/DocCardImage";

const AdminCategoryUpdateModal = ({
    open,
    onClose,
    documents = [],
    categories = [],
    loadingCategories = false,
    categoriesReady = false,
    sourceCategoryLabel = "",
    targetCategory = null,
    onTargetCategoryChange,
    onLoadCategories,
    onCategorySearchChange = () => {},
    onSubmit,
    submitting = false,
    onRemoveDocument,
}) => {
    const { t } = useTranslation();

    const isDocumentNew = (uploadedAt) => {
        if (!uploadedAt) return false;
        try {
            const uploadDate = new Date(uploadedAt);
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            return uploadDate >= threeMonthsAgo;
        } catch (_) {
            return false;
        }
    };

    const getFileExtension = (filename) => {
        if (!filename) return "";
        const parts = filename.split(".");
        return parts.length > 1 ? parts.pop().toLowerCase() : "";
    };

    const getDocumentId = (document) => document?.id || document?.document_id || null;

    const updateDisabled =
        submitting ||
        loadingCategories ||
        !targetCategory ||
        !Array.isArray(documents) ||
        documents.length === 0;

    const targetCategoryLabel =
        targetCategory?.display_name ||
        targetCategory?.category_name ||
        targetCategory?.label ||
        "";

    const summaryMessage = targetCategory
        ? t("MESSAGES.CATEGORY_REASSIGN_SUMMARY", {
            defaultValue: "{{count}} document(s) will move from {{source}} to {{target}}.",
            count: documents.length,
            source: sourceCategoryLabel || t("UNCATEGORIZED", { defaultValue: "Uncategorized" }),
            target: targetCategoryLabel || t("LABELS.CATEGORY", { defaultValue: "Category" }),
        })
        : t("MESSAGES.CATEGORY_REASSIGN_PROMPT", {
            defaultValue: "Choose a category to move the selected documents.",
        });

    return (
        <ModalWrapper
            open={open}
            setOpen={() => {}}
            onClose={onClose}
            width="72vw"
            title={t("BUTTON.UPDATE_CATEGORY", { defaultValue: "Update Category" })}
            content={
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
                        gap: 2,
                        height: "100%",
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                            borderRadius: 2,
                        }}
                    >
                        <Stack spacing={1} sx={{ mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                {documents.length} {t("LABELS.DOCUMENTS", { defaultValue: "documents" })} {t("LABELS.SELECTED", { defaultValue: "selected" })}
                            </Typography>
                        </Stack>

                        <Divider sx={{ mb: 1.5 }} />

                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                pr: 0.5,
                            }}
                        >
                            {documents.map((document, index) => (
                                <Box
                                    key={document.id || `${document.name}-${index}`}
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        "& .doc-remove-btn": {
                                            opacity: 0,
                                            pointerEvents: "none",
                                            transition: "opacity 0.15s ease",
                                        },
                                        "&:hover .doc-remove-btn": {
                                            opacity: 1,
                                            pointerEvents: "auto",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box sx={{ flexShrink: 0 }}>
                                            <DocCardImage
                                                filename={document.name || document.document_name}
                                                path={document.attachment}
                                                fileExtension={getFileExtension(document.name || document.document_name)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {document.name || document.document_name || "Untitled document"}
                                            </Typography>
                                            {isDocumentNew(document.uploaded_at) && (
                                                <Chip
                                                    label={t("LABELS.NEW", { defaultValue: "New" })}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.65rem",
                                                        fontWeight: 600,
                                                        backgroundColor: (theme) => theme.palette.primary.main,
                                                        color: "#ffffff",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Tooltip title={t("TOOLTIPS.REMOVE_DOCUMENT", { defaultValue: "Remove document" })} arrow>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    className="doc-remove-btn"
                                                    disabled={typeof onRemoveDocument !== "function"}
                                                    onClick={() => {
                                                        const docId = getDocumentId(document);
                                                        if (docId && typeof onRemoveDocument === "function") {
                                                            onRemoveDocument(docId);
                                                        }
                                                    }}
                                                    aria-label={t("TOOLTIPS.REMOVE_DOCUMENT", { defaultValue: "Remove document" })}
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: "error.main" },
                                                        p: 0.5,
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Stack spacing={1}>
                            <Typography variant="h6" fontWeight={600}>
                                {t("LABELS.CATEGORY", { defaultValue: "Category" })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("UI_TEXT.REASSIGN_CATEGORY_INSTRUCTIONS", {
                                    defaultValue: "Move the selected documents to an existing category.",
                                })}
                            </Typography>
                        </Stack>

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                {t("LABELS.CURRENT_CATEGORY", { defaultValue: "Current category" })}
                            </Typography>
                            <Chip
                                label={sourceCategoryLabel || t("UNCATEGORIZED", { defaultValue: "Uncategorized" })}
                                size="small"
                                color="default"
                                variant="outlined"
                            />
                        </Box>

                        <Autocomplete
                            options={categories}
                            value={targetCategory}
                            loading={loadingCategories}
                            onOpen={() => {
                                if (!categoriesReady && !loadingCategories) {
                                    onLoadCategories();
                                }
                            }}
                            onChange={(_, value) => onTargetCategoryChange(value)}
                            onInputChange={(_, value, reason) => {
                                if (reason !== "reset") {
                                    onCategorySearchChange(value);
                                }
                            }}
                            getOptionLabel={(option) => option?.display_name || option?.category_name || option?.label || ""}
                            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                            disablePortal
                            PopperProps={{
                                sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("BUTTON.ASSIGN", { defaultValue: "Assign Category" })}
                                        placeholder={t("PLACEHOLDERS.SELECT_CATEGORY", { defaultValue: "Select category" })}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingCategories ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText={
                                loadingCategories
                                    ? t("BUTTON.LOADING", { defaultValue: "Loading..." })
                                    : t("MESSAGES.NO_DOCUMENTS_FOUND", { defaultValue: "No categories found" })
                            }
                        />

                        <Alert severity={targetCategory ? "success" : "info"}>
                            {summaryMessage}
                        </Alert>
                    </Paper>
                </Box>
            }
            actions={
                <>
                    <Button onClick={onClose}>
                        {t("BUTTON.CANCEL", { defaultValue: "Cancel" })}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        disabled={updateDisabled}
                    >
                        {submitting
                            ? t("BUTTON.UPDATING", { defaultValue: "Updating..." })
                            : t("BUTTON.UPDATE_CATEGORY", { defaultValue: "Update Category" })}
                    </Button>
                </>
            }
        />
    );
};

export default AdminCategoryUpdateModal;
